import React from 'react'
import { GuidedHelp } from '../../../context/AdminExperienceContext'
import { useI18n } from '../../../i18n/I18nContext'

export type GuidedStepStatus = 'complete' | 'current' | 'todo' | 'blocked'

export interface GuidedWorkflowStep {
  id: string
  title: string
  description?: string
  status: GuidedStepStatus
  optional?: boolean
  blockingReason?: string
  helpText?: string
  primaryAction?: { label: string; onSelect: () => void; disabled?: boolean }
  secondaryAction?: { label: string; onSelect: () => void }
}

export interface GuidedWorkflowDefinition {
  id: string
  title: string
  description: string
  steps: GuidedWorkflowStep[]
}

const statusLabel: Record<GuidedStepStatus, string> = {
  complete: 'Complete',
  current: 'Current step',
  todo: 'Not started',
  blocked: 'Blocked',
}

export const GuidedSidePanel: React.FC<{ workflow: GuidedWorkflowDefinition }> = ({ workflow }) => {
  const { t } = useI18n()
  const completed = workflow.steps.filter((step) => step.status === 'complete').length
  return (
    <GuidedHelp>
      <aside className="panel workspace-panel guided-workflow-panel stack" aria-labelledby={`${workflow.id}-title`} data-testid={`guided-workflow-${workflow.id}`}>
        <div className="admin-panel-toolbar">
          <div className="stack" style={{ gap: '.2rem' }}>
            <h3 id={`${workflow.id}-title`} style={{ margin: 0 }}>{workflow.title}</h3>
            <p className="text-muted">{workflow.description}</p>
          </div>
          <span className="status-pill">{completed} / {workflow.steps.length}</span>
        </div>
        <ol className="guided-process-list">
          {workflow.steps.map((step, index) => (
            <li key={step.id} className={`guided-process-step guided-process-step--${step.status}`} aria-current={step.status === 'current' ? 'step' : undefined}>
              <span className="guided-process-step__marker" aria-hidden="true">{step.status === 'complete' ? '✓' : index + 1}</span>
              <div className="stack" style={{ gap: '.2rem' }}>
                <div><strong>{step.title}</strong>{step.optional ? <span className="helper"> · Optional</span> : null}</div>
                <span className="visually-hidden">{t(statusLabel[step.status])}</span>
                {step.description ? <p className="helper">{step.description}</p> : null}
                {step.blockingReason ? <p className="form-error" role="status">{step.blockingReason}</p> : null}
                {step.helpText ? <p className="helper">{step.helpText}</p> : null}
                {(step.primaryAction || step.secondaryAction) ? <div className="inline-group">
                  {step.primaryAction ? <button type="button" className="btn btn-primary" disabled={step.primaryAction.disabled} onClick={step.primaryAction.onSelect}>{step.primaryAction.label}</button> : null}
                  {step.secondaryAction ? <button type="button" className="btn btn-secondary" onClick={step.secondaryAction.onSelect}>{step.secondaryAction.label}</button> : null}
                </div> : null}
              </div>
            </li>
          ))}
        </ol>
      </aside>
    </GuidedHelp>
  )
}

export const GuidedCoachMark: React.FC<{
  title: string
  description: string
  action?: { label: string; onSelect: () => void; disabled?: boolean }
  blockingReason?: string
}> = ({ title, description, action, blockingReason }) => (
  <GuidedHelp>
    <div className={`admin-state ${blockingReason ? 'admin-state--warning' : 'admin-state--info'}`} role="note">
      <strong>{title}</strong>
      <p>{description}</p>
      {blockingReason ? <p className="form-error">{blockingReason}</p> : null}
      {action ? <button type="button" className="btn btn-primary" disabled={action.disabled} onClick={action.onSelect}>{action.label}</button> : null}
    </div>
  </GuidedHelp>
)
